import {
  nullableCreatedByUserUuid,
  timestamps,
  uuid,
} from '@snap-types/models/common';
import {
  PriceSegmentType,
  priceSegmentTypeSchema,
} from '@snap-types/models/price-segment/enums';
import { schema } from '@snap-types/schemas';
import { z } from 'zod/v4';

const d = schema.t.dec;

export const columns = {
  uuid,
  sortOrder: z.int().default(0),
  priceSegmentType: priceSegmentTypeSchema,
  priceStrategyConfigUuid: uuid,
  enabled: z.boolean().default(true),

  // CustomerPotential fields
  low: d.fromUnknown.nullish(),
  medium: d.fromUnknown.nullish(),
  high: d.fromUnknown.nullish(),

  // CustomerPortals fields
  portalsAdjustment: d.fromUnknown.nullish(),

  // ProductOperationsCount fields
  threshold: z.int().nullish(),
  ltAdjustment: d.fromUnknown.nullish(),
  gteAdjustment: d.fromUnknown.nullish(),

  // ProductComplexity fields
  simple: d.fromUnknown.nullish(),
  moderate: d.fromUnknown.nullish(),
  complex: d.fromUnknown.nullish(),

  // CustomerTerms, MarketIndustry, LastTransactionAge, LastGrossMargin
  defaultAdjustment: d.fromUnknown.nullish(),

  ...nullableCreatedByUserUuid,
  ...timestamps,

  deletedAt: z.date().nullish(),
} as const;

export const customerPotentialColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.CustomerPotential),
  low: d.fromUnknown,
  medium: d.fromUnknown,
  high: d.fromUnknown,
});

export const customerPortalsColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.CustomerPortals),
  portalsAdjustment: d.fromUnknown,
});

export const customerTermsColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.CustomerTerms),
  defaultAdjustment: d.fromUnknown,
});

export const productOperationsCountColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.ProductOperationsCount),
  threshold: schema.t.num.fromString.pipe(
    z.int({ error: 'Must be an integer' }),
  ),
  ltAdjustment: d.fromUnknown,
  gteAdjustment: d.fromUnknown,
});

export const productComplexityColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.ProductComplexity),
  simple: d.fromUnknown,
  moderate: d.fromUnknown,
  complex: d.fromUnknown,
});

export const marketIndustryColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.MarketIndustry),
  defaultAdjustment: d.fromUnknown,
});

export const lastTransactionAgeColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.LastTransactionAge),
  defaultAdjustment: d.fromUnknown,
});

export const lastGrossMarginColumns = z.object({
  priceSegmentType: z.literal(PriceSegmentType.LastGrossMargin),
  defaultAdjustment: d.fromUnknown,
});
